import type { Database, Json } from '@/lib/supabase/types.gen';
import type {
  BlankCanvasMetadata,
  FloorPlanGeometryPoint,
  ISODateString,
  PropertyFloorAvailablePolygon,
  PropertyFloorRecord,
  UnitAvailabilityState,
  UnitAvailabilityStatus,
  UnitFormValues,
  UnitListMetrics,
  UnitPossessionTimeline,
  UnitRentPricing,
  UnitSummary,
} from '@/lib/types/property';
import { normalizeAnnualRentPerSf } from '@/lib/utils/rentCalculations';

type UnitsTable = Database['public']['Tables']['units'];
export type UnitRow = UnitsTable['Row'];

export const LEASEABLE_UNIT_STATUSES: UnitAvailabilityStatus[] = ['available'];

function normalizePossessionTimeline(value: unknown): UnitPossessionTimeline {
  return value === 'negotiable' ? 'negotiable' : 'immediate';
}

export interface PropertyUnitAggregate {
  publishedUnitCount: number;
  hasVacancy: boolean;
  hasIncentives: boolean;
  hasPotentiallyDivisible: boolean;
  readinessStates: UnitAvailabilityStatus[];
  minUnitRentableSf: number | null;
  maxUnitRentableSf: number | null;
  minNetRent: number | null;
  maxNetRent: number | null;
  minAdditionalRent: number | null;
  maxAdditionalRent: number | null;
  earliestAvailability: ISODateString | null;
  latestAvailability: ISODateString | null;
}

export function mapUnitRowToSummary(row: UnitRow): UnitSummary {
  const unitLabel = typeof row.unit_label === 'string' ? row.unit_label : row.unit_code;
  const rentFrequency = row.rent_frequency;
  return {
    id: row.id,
    propertyId: row.property_id,
    unitCode: row.unit_code,
    unitLabel,
    floorLevel: row.floor_level,
    areaSf: row.area_sf,
    minDivisibleSf: row.min_divisible_sf ?? row.area_sf,
    availabilityStart: row.availability_start,
    availabilityEnd: row.availability_end,
    availability: createAvailabilityState(row.availability_status, row.status_updated_at),
    rent: createRentPricing(row, rentFrequency),
    rentFrequency: 'annually',
    incentives: row.incentives,
    potentiallyDivisible: row.potentially_divisible,
    contactEmail: row.contact_email,
    galleryImageUrls: Array.isArray(row.gallery_image_urls) ? row.gallery_image_urls : [],
    heroImageUrl: row.hero_image_url,
    planGeometry: row.plan_geometry,
    possessionTimeline: normalizePossessionTimeline(row.possession_timeline),
    displayOnPublicWebsite: row.display_on_public_website,
    published: row.published,
    statusUpdatedAt: row.status_updated_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function mapUnitRowToFormValues(row: UnitRow): UnitFormValues {
  const unitLabel = typeof row.unit_label === 'string' ? row.unit_label : row.unit_code;
  const rentFrequency = row.rent_frequency;
  return {
    id: row.id,
    propertyId: row.property_id,
    unitCode: row.unit_code,
    unitLabel,
    floorLevel: row.floor_level ?? null,
    areaSf: row.area_sf,
    minDivisibleSf: row.min_divisible_sf,
    availabilityStart: row.availability_start,
    availabilityEnd: row.availability_end,
    availabilityStatus: row.availability_status,
    possessionTimeline: normalizePossessionTimeline(row.possession_timeline),
    rentBasis: row.rent_basis,
    netRentPerSf: normalizeAnnualRentPerSf(row.net_rent_per_sf, rentFrequency),
    additionalRentPerSf: normalizeAnnualRentPerSf(row.additional_rent_per_sf, rentFrequency),
    rentFrequency: 'annually',
    incentives: row.incentives,
    potentiallyDivisible: row.potentially_divisible,
    contactEmail: row.contact_email,
    galleryImageUrls: Array.isArray(row.gallery_image_urls) ? row.gallery_image_urls : [],
    heroImageUrl: row.hero_image_url,
    planGeometry: row.plan_geometry,
    displayOnPublicWebsite: row.display_on_public_website,
    published: row.published,
  };
}

export function mapUnitSummaryToFormValues(summary: UnitSummary): UnitFormValues {
  return {
    id: summary.id,
    propertyId: summary.propertyId,
    unitCode: summary.unitCode,
    unitLabel: summary.unitLabel,
    floorLevel: summary.floorLevel,
    areaSf: summary.areaSf,
    minDivisibleSf: summary.minDivisibleSf,
    availabilityStart: summary.availabilityStart,
    availabilityEnd: summary.availabilityEnd,
    availabilityStatus: summary.availability.status,
    possessionTimeline: normalizePossessionTimeline(summary.possessionTimeline),
    rentBasis: summary.rent.rentBasis,
    netRentPerSf: summary.rent.netRentPerSf,
    additionalRentPerSf: summary.rent.additionalRentPerSf,
    rentFrequency: 'annually',
    incentives: summary.incentives,
    potentiallyDivisible: summary.potentiallyDivisible,
    contactEmail: summary.contactEmail,
    galleryImageUrls: summary.galleryImageUrls,
    heroImageUrl: summary.heroImageUrl,
    planGeometry: summary.planGeometry,
    displayOnPublicWebsite: summary.displayOnPublicWebsite,
    published: summary.published,
  };
}

export function createPropertyUnitAggregate(unitRows: UnitRow[]): PropertyUnitAggregate {
  const readinessStates = new Set<UnitAvailabilityStatus>();
  let publishedUnitCount = 0;
  let hasVacancy = false;
  let hasIncentives = false;
  let hasPotentiallyDivisible = false;
  let minUnitRentableSf: number | null = null;
  let maxUnitRentableSf: number | null = null;
  let minNetRent: number | null = null;
  let maxNetRent: number | null = null;
  let minAdditionalRent: number | null = null;
  let maxAdditionalRent: number | null = null;
  let earliestAvailability: ISODateString | null = null;
  let latestAvailability: ISODateString | null = null;

  for (const row of unitRows) {
    const rentFrequency = row.rent_frequency;
    const normalizedNetRent = normalizeAnnualRentPerSf(row.net_rent_per_sf, rentFrequency);
    if (row.display_on_public_website) {
      publishedUnitCount += 1;
    }

    readinessStates.add(row.availability_status);

    if (
      row.display_on_public_website &&
      LEASEABLE_UNIT_STATUSES.includes(row.availability_status)
    ) {
      hasVacancy = true;
    }

    if (!hasIncentives && typeof row.incentives === 'string' && row.incentives.trim().length > 0) {
      hasIncentives = true;
    }

    if (!hasPotentiallyDivisible && row.potentially_divisible) {
      hasPotentiallyDivisible = true;
    }

    minUnitRentableSf =
      minUnitRentableSf === null ? row.area_sf : Math.min(minUnitRentableSf, row.area_sf);
    maxUnitRentableSf =
      maxUnitRentableSf === null ? row.area_sf : Math.max(maxUnitRentableSf, row.area_sf);

    minNetRent = minNetRent === null ? normalizedNetRent : Math.min(minNetRent, normalizedNetRent);
    maxNetRent = maxNetRent === null ? normalizedNetRent : Math.max(maxNetRent, normalizedNetRent);

    if (row.rent_basis !== 'gross') {
      const additionalRent = normalizeAnnualRentPerSf(
        row.additional_rent_per_sf ?? 0,
        rentFrequency,
      );
      minAdditionalRent =
        minAdditionalRent === null ? additionalRent : Math.min(minAdditionalRent, additionalRent);
      maxAdditionalRent =
        maxAdditionalRent === null ? additionalRent : Math.max(maxAdditionalRent, additionalRent);
    }

    earliestAvailability = pickEarlierDate(earliestAvailability, row.availability_start);
    latestAvailability = pickLaterDate(latestAvailability, row.availability_start);
  }

  return {
    publishedUnitCount,
    hasVacancy,
    hasIncentives,
    hasPotentiallyDivisible,
    readinessStates: Array.from(readinessStates),
    minUnitRentableSf,
    maxUnitRentableSf,
    minNetRent,
    maxNetRent,
    minAdditionalRent,
    maxAdditionalRent,
    earliestAvailability,
    latestAvailability,
  };
}

export function createUnitListMetrics(unitRows: UnitRow[]): UnitListMetrics {
  let published = 0;
  let leaseable = 0;

  for (const row of unitRows) {
    if (row.published) {
      published += 1;
    }
    if (
      row.display_on_public_website &&
      LEASEABLE_UNIT_STATUSES.includes(row.availability_status)
    ) {
      leaseable += 1;
    }
  }

  return {
    total: unitRows.length,
    published,
    leaseable,
  };
}

export function updateUnitSummaryFromForm(
  summary: UnitSummary,
  values: UnitFormValues,
  options: { statusUpdatedAt?: ISODateString } = {},
): UnitSummary {
  const nextStatus = values.availabilityStatus;
  const statusUpdatedAt = options.statusUpdatedAt ?? new Date().toISOString();
  const availability = createAvailabilityState(nextStatus, statusUpdatedAt);
  const baseAdditional = summary.rent.additionalRentPerSf ?? 0;
  const rent: UnitRentPricing =
    values.rentBasis === 'gross'
      ? {
          rentBasis: 'gross',
          netRentPerSf: values.netRentPerSf,
          additionalRentPerSf: null,
        }
      : {
          rentBasis: values.rentBasis,
          netRentPerSf: values.netRentPerSf,
          additionalRentPerSf: values.additionalRentPerSf ?? baseAdditional,
        };

  return {
    ...summary,
    unitCode: values.unitCode,
    unitLabel: values.unitLabel,
    floorLevel: values.floorLevel ?? null,
    areaSf: values.areaSf,
    minDivisibleSf: values.minDivisibleSf ?? summary.minDivisibleSf,
    availabilityStart: summary.availabilityStart,
    availabilityEnd: summary.availabilityEnd,
    availability,
    rent,
    rentFrequency: 'annually',
    incentives: values.incentives ?? null,
    potentiallyDivisible: values.potentiallyDivisible,
    contactEmail: values.contactEmail,
    heroImageUrl:
      values.heroImageUrl === undefined ? (summary.heroImageUrl ?? null) : values.heroImageUrl,
    galleryImageUrls: values.galleryImageUrls ?? summary.galleryImageUrls,
    planGeometry: values.planGeometry ?? summary.planGeometry,
    possessionTimeline: normalizePossessionTimeline(values.possessionTimeline),
    displayOnPublicWebsite: values.displayOnPublicWebsite,
    published: values.published,
    statusUpdatedAt,
    updatedAt: statusUpdatedAt,
  } satisfies UnitSummary;
}

export function createUnitSummaryFromValues(
  values: UnitFormValues,
  options: { statusUpdatedAt?: ISODateString; id?: string } = {},
): UnitSummary {
  const statusUpdatedAt = options.statusUpdatedAt ?? new Date().toISOString();
  const rent: UnitRentPricing =
    values.rentBasis === 'gross'
      ? {
          rentBasis: 'gross',
          netRentPerSf: values.netRentPerSf,
          additionalRentPerSf: null,
        }
      : {
          rentBasis: values.rentBasis,
          netRentPerSf: values.netRentPerSf,
          additionalRentPerSf: values.additionalRentPerSf ?? 0,
        };

  return {
    id: options.id ?? values.id ?? generateUnitIdentifier(),
    propertyId: values.propertyId,
    unitCode: values.unitCode,
    unitLabel: values.unitLabel,
    floorLevel: values.floorLevel ?? null,
    areaSf: values.areaSf,
    minDivisibleSf: values.minDivisibleSf ?? values.areaSf,
    availabilityStart: statusUpdatedAt,
    availabilityEnd: null,
    availability: createAvailabilityState(values.availabilityStatus, statusUpdatedAt),
    rent,
    rentFrequency: 'annually',
    incentives: values.incentives ?? null,
    potentiallyDivisible: values.potentiallyDivisible,
    contactEmail: values.contactEmail,
    heroImageUrl: values.heroImageUrl ?? null,
    galleryImageUrls: values.galleryImageUrls ?? [],
    planGeometry: values.planGeometry ?? null,
    possessionTimeline: normalizePossessionTimeline(values.possessionTimeline),
    displayOnPublicWebsite: values.displayOnPublicWebsite,
    published: values.published,
    statusUpdatedAt,
    createdAt: statusUpdatedAt,
    updatedAt: statusUpdatedAt,
  } satisfies UnitSummary;
}

function generateUnitIdentifier(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `temp-${Date.now().toString()}`;
}

export function mapFloorRowToRecord(
  row: Database['public']['Tables']['property_floors']['Row'],
): PropertyFloorRecord {
  return {
    id: row.id,
    propertyId: row.property_id,
    floorLevel: row.floor_level ?? null,
    totalFloorSf: row.total_floor_sf,
    allocatedUnitSf: row.allocated_unit_sf,
    unallocatedSf: row.unallocated_sf ?? Math.max(row.total_floor_sf - row.allocated_unit_sf, 0),
    availablePolygons: coercePolygonArray(row.available_polygons),
    drawConfig: coerceBlankCanvasMetadata(row.draw_config),
    metadata: coerceBlankCanvasMetadata(row.metadata),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function coercePolygonArray(value: Json | null): PropertyFloorAvailablePolygon[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const candidates = value as unknown[];
  return candidates.filter(isPropertyFloorPolygon);
}

function isPropertyFloorPolygon(value: unknown): value is PropertyFloorAvailablePolygon {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  if (!('points' in value)) {
    return false;
  }

  const points = (value as { points?: unknown }).points;
  if (!Array.isArray(points)) {
    return false;
  }

  return points.every(isPointTuple);
}

function isPointTuple(value: unknown): value is FloorPlanGeometryPoint {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    value.every((coordinate) => typeof coordinate === 'number' && Number.isFinite(coordinate))
  );
}

function coerceBlankCanvasMetadata(value: Json | null): BlankCanvasMetadata {
  if (value !== null && typeof value === 'object') {
    return value as BlankCanvasMetadata;
  }
  return {};
}

function createAvailabilityState(
  status: UnitAvailabilityStatus,
  statusUpdatedAt: ISODateString,
): UnitAvailabilityState {
  if (status === 'available') {
    return {
      status,
      leaseable: true,
      statusUpdatedAt,
    };
  }

  return {
    status,
    leaseable: false,
    statusUpdatedAt,
  };
}

function createRentPricing(
  row: UnitRow,
  rentFrequency: UnitRow['rent_frequency'],
): UnitRentPricing {
  const netRentPerSf = normalizeAnnualRentPerSf(row.net_rent_per_sf, rentFrequency);
  if (row.rent_basis === 'gross') {
    return {
      rentBasis: 'gross',
      netRentPerSf,
      additionalRentPerSf: null,
    };
  }

  return {
    rentBasis: row.rent_basis,
    netRentPerSf,
    additionalRentPerSf: normalizeAnnualRentPerSf(row.additional_rent_per_sf ?? 0, rentFrequency),
  };
}

function pickEarlierDate(
  existing: ISODateString | null,
  next: ISODateString | null,
): ISODateString | null {
  if (next === null) {
    return existing;
  }
  if (existing === null) {
    return next;
  }
  return new Date(next).getTime() < new Date(existing).getTime() ? next : existing;
}

function pickLaterDate(
  existing: ISODateString | null,
  next: ISODateString | null,
): ISODateString | null {
  if (next === null) {
    return existing;
  }
  if (existing === null) {
    return next;
  }
  return new Date(next).getTime() > new Date(existing).getTime() ? next : existing;
}

export type { UnitRow };
